/**
 * The beta editor's fields: one panel per block instance with fillable
 * slots (`describeCompositionForm`), conditional visibility from
 * `computeFormState`, and the legacy field inputs. Autofill-bound fields
 * get the Automatisk / Manuell switch instead of a plain input.
 */

import {
  autofillMode,
  type CompositionForm,
  type FormField,
  type FormState,
  type RuleIssue,
} from '@hyranai/regta-document-ir';
import { Fragment, type ReactNode } from 'react';
import {
  RENDERERS,
  type AttachmentCreateSeed,
  type AttachmentRef,
  type DataTableColumn,
  type DataTableRow,
} from '@hyranai/regta-templates';
import { AutofillModeField, type AutofillMode } from '../../components/autofill/AutofillModeField.js';
import { dataTableXlsxIO } from '../../lib/data-table-xlsx.js';
import { locateAnchor } from '../../lib/locate-anchor.js';
import {
  autofillSourceLabel,
  displayValue,
  toEditorField,
  type EditorField,
} from './form-model.js';
import { RuleMessage, type RuleActions } from './RuleMessages.js';

const MAPPED_SOURCE_LABEL = 'Hämtas automatiskt från huvudavtalet.';

const multilineAutofill = (field: FormField): boolean =>
  field.type === 'textarea' ||
  field.autofill?.kind === 'property_list' ||
  (field.autofill?.kind === 'party' && field.autofill.field === 'address');

export function BetaFormGroups({
  form,
  state,
  values,
  scope,
  autoValues,
  legacyFields,
  attachments,
  disabled,
  modeLocked,
  busyKey,
  onFieldChange,
  onSwitchMode,
  onFocusInstance,
  onCreateAttachment,
  dataTableAutoImport,
  isGapShown,
  onFieldBlur,
  beforeField,
  ruleIssues = [],
  ruleActions,
  mappedFromParent = false,
}: {
  form: CompositionForm;
  state: FormState;
  values: Record<string, unknown>;
  /** Values the visibility/missing state was computed against. */
  scope: Record<string, unknown>;
  autoValues: Record<string, unknown>;
  legacyFields: ReadonlyMap<string, EditorField>;
  attachments: ReadonlyArray<AttachmentRef>;
  disabled: boolean;
  modeLocked: boolean;
  busyKey: string | null;
  onFieldChange: (key: string, next: unknown) => void;
  onSwitchMode: (key: string, mode: AutofillMode) => void;
  onFocusInstance: (instanceId: string) => void;
  onCreateAttachment?: (seed: AttachmentCreateSeed) => Promise<string | null>;
  dataTableAutoImport?: (columns: ReadonlyArray<DataTableColumn>) => Promise<DataTableRow[] | null>;
  /** Whether a gap on this key is shown yet (touched, or revealed by a step transition). */
  isGapShown: (key: string) => boolean;
  /** Focus left the field's control. */
  onFieldBlur: (key: string) => void;
  /** Rendered just before a field — the party/property picker that fills it. */
  beforeField?: (key: string) => ReactNode;
  /** Shown validation rule issues (ADR-0101); each renders under its anchor field. */
  ruleIssues?: ReadonlyArray<RuleIssue>;
  ruleActions?: RuleActions;
  /**
   * A template bilaga's form (ADR-0102 addendum 3): fields mapped from the
   * main document (`derivedFrom` / `derivedFromParent`) get the same
   * Automatisk / Manuell switch as autofilled ones.
   */
  mappedFromParent?: boolean;
}) {
  const isMapped = (f: FormField): boolean => mappedFromParent && !f.autofill && Boolean(f.derivedFrom || f.derivedFromParent);
  const isBound = (f: FormField): boolean => Boolean(f.autofill) || isMapped(f);
  const rulesByKey = new Map<string, RuleIssue[]>();
  for (const issue of ruleIssues) {
    rulesByKey.set(issue.anchorKey, [...(rulesByKey.get(issue.anchorKey) ?? []), issue]);
  }
  const visibleKeys = new Set(state.visibleKeys);
  const visibleGroups = new Set(state.visibleGroups);
  const missingKeys = new Set(state.missing.map((f) => f.key));
  const statutoryKeys = new Set(state.missingStatutory.map((f) => f.key));

  return (
    <>
      {form.groups
        .filter((g) => visibleGroups.has(g.instanceId))
        .map((group) => {
          const fields = group.fields.filter(
            (f) => visibleKeys.has(f.key) && !f.key.startsWith('__'),
          );
          const rendered = fields
            .map((f) => ({ f, editor: isBound(f) ? null : toEditorField(f, legacyFields.get(f.key)) }))
            .filter(({ f, editor }) => isBound(f) || editor);
          if (rendered.length === 0) return null;
          return (
            <fieldset
              key={group.instanceId}
              data-instance-id={group.instanceId}
              onFocusCapture={() => onFocusInstance(group.instanceId)}
              className="flex flex-col gap-3 rounded border border-stone-200 p-3"
            >
              <legend className="px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                {group.title}
              </legend>
              {group.custom && (
                <p className="text-[11px] text-stone-500">
                  Egna fält som används i den anpassade texten. Namn och typ ändras under Anpassa text.
                </p>
              )}
              {rendered.map(({ f, editor }) => {
                const missing = missingKeys.has(f.key) && isGapShown(f.key);
                const rules = rulesByKey.get(f.key) ?? [];
                const ruleError = rules.some((r) => r.severity === 'error' && !r.overridden);
                const statutory = statutoryKeys.has(f.key);
                return (
                  <Fragment key={f.key}>
                    {beforeField?.(f.key)}
                    <div
                      data-locate-anchor={locateAnchor('field', f.key)}
                      data-gap={missing ? 'shown' : undefined}
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFieldBlur(f.key);
                      }}
                      className={
                        'flex flex-col gap-1 ' +
                        (missing || ruleError
                          ? 'border-l-2 border-red-300 pl-2'
                          : rules.some((r) => r.severity === 'warning')
                            ? 'border-l-2 border-amber-300 pl-2'
                            : '')
                      }
                    >
                      {isBound(f) ? (
                        <AutofillModeField
                          id={`bf-${f.key}`}
                          label={f.label}
                          mode={autofillMode(values, f.key)}
                          text={String(
                            (autofillMode(values, f.key) === 'auto'
                              ? (autoValues[f.key] ?? scope[f.key])
                              : values[f.key]) ?? '',
                          )}
                          required={f.required || f.statutory}
                          missing={missing}
                          multiline={multilineAutofill(f)}
                          disabled={disabled}
                          modeLocked={modeLocked}
                          busy={busyKey === f.key}
                          emptyAutoText={(isMapped(f) ? MAPPED_SOURCE_LABEL : autofillSourceLabel(f, form.partySlots)).replace(
                            /^Hämtas automatiskt/,
                            'Fylls i automatiskt',
                          )}
                          autoHelp={isMapped(f) ? MAPPED_SOURCE_LABEL : autofillSourceLabel(f, form.partySlots)}
                          manualHelp={
                            isMapped(f)
                              ? 'Egen text — följer inte längre huvudavtalet.'
                              : 'Egen text — skrivs inte över när parter eller fastigheter ändras.'
                          }
                          onSwitch={(mode) => onSwitchMode(f.key, mode)}
                          onTextChange={(next) => onFieldChange(f.key, next)}
                        />
                      ) : (
                        (() => {
                          const Renderer = RENDERERS[editor!.type];
                          return (
                            <Renderer
                              field={editor!}
                              value={displayValue(f, values[f.key] ?? form.defaults[f.key], legacyFields.get(f.key))}
                              allValues={scope}
                              dataTableXlsx={dataTableXlsxIO}
                              dataTableAutoImport={dataTableAutoImport}
                              onChange={(next) => onFieldChange(f.key, next)}
                              disabled={disabled}
                              attachments={attachments}
                              onCreateAttachment={
                                editor!.type === 'attachment_ref' && !disabled
                                  ? onCreateAttachment
                                  : undefined
                              }
                            />
                          );
                        })()
                      )}
                      {missing && (
                        <span role="alert" className="text-[11px] text-red-700">
                          {statutory ? 'Lagstadgad uppgift saknas.' : 'Obligatorisk uppgift saknas.'}
                        </span>
                      )}
                      {ruleActions &&
                        rules.map((issue) => (
                          <RuleMessage key={issue.id} issue={issue} variant="field" actions={ruleActions} />
                        ))}
                    </div>
                  </Fragment>
                );
              })}
            </fieldset>
          );
        })}
    </>
  );
}
